import _ from "lodash";
import React, {useState, useEffect, useMemo, useRef} from "react";
import {TextField, IconButton, Tooltip, Box, Button} from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import InfoIcon from "@material-ui/icons/Info";
import {getEntityFromLocationSearch} from "routes/data_assets/util/get_entity_from_url";
import {getFiltersFromLocationSearch} from "routes/data_assets/util/get_dates_from_url";
import DefinitionSection from "./definition";
import ConfigurationSection from "./configuration";
import {ConfigurationRunOutput} from "./configuration_run_outputs";
import {
  getQueryParams,
  isDataAsset,
} from "../containers/data_assets_create_view";

import {
  DataKey,
  WorkflowInputValues,
  WorkflowInstanceConfig,
} from "common/flowmaster/types/workflow";
import {Uuid} from "common/types";
import ConfigurationRuns from "./configuration_runs";
import {
  DataAssetRunInfo,
  Definition,
  DefinitionConfig,
} from "common/flowmaster/types/data_asset";
import {EnhancedLlmRun, LlmRunId} from "common/flowmaster/types/llm_run";
import {TestId} from "common/flowmaster/types/test";
import {RefreshWithFeedbackButton} from "routes/data_assets/util/refresh_with_feedback_button";
import DatasetIcon from "icons/Dataset";
import DatasetLinkedIcon from "icons/DatasetLinked";
import {MaterialiseSplitButton} from "./materialise_split_button";

import {RunWithFeedback} from "routes/data_assets/util/run_with_feedback_button";
import ComparisonLink from "routes/data_assets/util/comparison_link";
import TestListLink from "routes/data_assets/util/test_list_link";

interface DataAssetsCreateViewProps {
  definitions: Definition[];
  createDefinitionConfig: (
    config: WorkflowInstanceConfig,
    name: string,
    groupId?: Uuid,
    archive?: boolean,
  ) => Promise<void>;
  updateAllConfigNames: (name: string) => void;
  materialiseDataAsset: (
    assetName: string,
    latestConfigId?: DefinitionConfig["id"],
    materialiseWithLatestDependencies?: boolean,
  ) => void;
  push: (path: string) => void;
  location: {
    pathname: string;
    search: string;
  };
  params: {
    definitionId?: string;
    configId?: string;
    assetId?: string;
    organisationId: string;
  };
  configRuns: DataAssetRunInfo[];
  isLoadingConfigRuns: boolean;
  isRefreshingRuns: boolean;
  fetchConfigRuns: (refresh?: boolean) => void;
  fetchConfigRunsError: string | null;
  clearConfigRuns: () => void;
  updateConfigIsArchived: (isArchived: boolean) => void;
  clearAssetPath: () => void;
  updateConfigIsPublic: (isPublic: boolean) => void;
  updateConfigDefaultAssetName: (defaultAssetName: string) => void;
  onAddTest: (dataKey: DataKey, key: string) => void;
  onUpdateTest: (
    testId: TestId,
    testType: string,
    value: Record<string, unknown>,
  ) => void;
  onDeleteTest: (testId: TestId) => void;
  rerunTests: (runId: LlmRunId) => void;
  rerunAllTests: (runIds: LlmRunId[]) => void;
  rerunLlmRun: () => void;
  getTestResults: (dataKey: DataKey, key: string) => void;
  fetchLlmRun: (organisationId: number, runId: LlmRunId) => void;
  selectedLlmRun: EnhancedLlmRun | null;
  showGroupRuns: boolean;
  setShowGroupRuns: (value: boolean) => void;
  updateValues: (pairs: Record<string, unknown | undefined | null>) => void;
  isRerunningTests: boolean;
  isRefreshingRun: boolean;
}

const DataAssetsCreateView: React.FC<DataAssetsCreateViewProps> = props => {
  const {
    definitions,
    createDefinitionConfig,
    push,
    location,
    params,
    updateAllConfigNames,
    materialiseDataAsset,
    configRuns,
    isLoadingConfigRuns,
    isRefreshingRuns,
    fetchConfigRuns,
    fetchConfigRunsError,
    clearConfigRuns,
    updateConfigIsArchived,
    clearAssetPath,
    updateConfigIsPublic,
    updateConfigDefaultAssetName,
    onAddTest,
    onUpdateTest,
    onDeleteTest,
    rerunTests,
    rerunAllTests,
    getTestResults,
    fetchLlmRun,
    selectedLlmRun,
    showGroupRuns,
    setShowGroupRuns,
    updateValues,
    isRerunningTests,
    isRefreshingRun,
  } = props;

  const organisationId = parseInt(props.params.organisationId, 10);

  const [showRerunAllTestsFeedback, setShowRerunAllTestsFeedback] = useState(
    false,
  );
  const [showStartedFeedback, setShowStartedFeedback] = useState(false);
  const [
    showStartedWithLatestFeedback,
    setShowStartedWithLatestFeedback,
  ] = useState(false);
  const [selectedDefinitionId, setSelectedDefinitionId] = useState<
    number | null
  >(parseInt(params.definitionId ?? "", 10) ?? null);
  const [selectedConfigId, setSelectedConfigId] = useState<number | string>(
    params.configId ?? "",
  );
  const [configHasChanges, setConfigHasChanges] = useState(false);
  const [assetName, setAssetName] = useState("");
  const basePath = location.pathname.split(/\/(create|definition|config)/)[0];

  const filteredDefinitions = useMemo(() => {
    const referencedConfigIds = new Set<string>();
    const referencedConfigGroupIds = new Set<string>();
    const referencedDefinitionIds = new Set<string>();
    const referencedDefinitionGroupIds = new Set<string>();

    if (params.configId) {
      referencedConfigIds.add(params.configId);
    }

    if (params.definitionId) {
      referencedDefinitionIds.add(params.definitionId);
    }

    const currentSelectedConfig = definitions
      .flatMap(d => d.workflow_instances || [])
      .find(c => c.id === Number(selectedConfigId));

    Object.values(currentSelectedConfig?.config?.inputs?.values || {})
      .filter(isDataAsset)
      .forEach(value => {
        if (value.config_id) {
          referencedConfigIds.add(value.config_id);
        }
        if (value.config_group_id) {
          referencedConfigGroupIds.add(value.config_group_id);
        }
        if (value.definition_id) {
          referencedDefinitionIds.add(value.definition_id);
        }
        if (value.definition_group_id) {
          referencedDefinitionGroupIds.add(value.definition_group_id);
        }
      });

    const shouldIncludeConfig = (config: DefinitionConfig) =>
      !config.is_archived ||
      referencedConfigIds.has(config.id.toString()) ||
      (config.group_id && referencedConfigGroupIds.has(config.group_id));

    const shouldIncludeDefinition = (definition: Definition) =>
      !definition.is_archived ||
      referencedDefinitionIds.has(definition.id.toString()) ||
      (definition.group_id &&
        referencedDefinitionGroupIds.has(definition.group_id));

    return definitions
      .filter(
        definition =>
          definition.is_locked && shouldIncludeDefinition(definition),
      )
      .map(definition => ({
        ...definition,
        workflow_instances:
          definition.workflow_instances?.filter(shouldIncludeConfig) || [],
      }))
      .filter(definition => {
        if (referencedDefinitionIds.has(definition.id.toString())) {
          return true;
        }

        if (definition.group_id) {
          const definitionsInGroup = definitions.filter(
            d => d.group_id === definition.group_id,
          );

          const hasNonArchivedInGroup = definitionsInGroup.some(
            d => !d.is_archived,
          );
          const isGroupReferenced = referencedDefinitionGroupIds.has(
            definition.group_id,
          );

          return hasNonArchivedInGroup || isGroupReferenced;
        }

        return true;
      });
  }, [definitions, selectedConfigId, params.configId, params.definitionId]);

  const selectedDefinition = filteredDefinitions.find(
    d => d.id === Number(selectedDefinitionId),
  );
  const groupDefinitions = filteredDefinitions.filter(
    def => def.group_id === selectedDefinition?.group_id,
  );
  const latestDefinition = _.maxBy(groupDefinitions, "creation_date");
  const isLatestDefinition = latestDefinition?.id === selectedDefinitionId;

  const configurations = selectedDefinition?.workflow_instances || [];
  const selectedConfig = configurations.find(
    c => c.id === Number(selectedConfigId),
  );

  const matchingLatestDefinitionConfig = isLatestDefinition
    ? selectedConfig
    : _.maxBy(
        latestDefinition?.workflow_instances?.filter(
          config => config.group_id === selectedConfig?.group_id,
        ),
        "creation_date",
      );

  const [projectId, setProjectId] = useState(
    getEntityFromLocationSearch(location.search).projectId,
  );
  const [documentId, setDocumentId] = useState(
    getEntityFromLocationSearch(location.search).documentId,
  );

  const [filters, setFilters] = useState(
    _.pick(getFiltersFromLocationSearch(location.search), ["from", "to"]),
  );

  const view = new URLSearchParams(location.search).get("view");

  const [selectedRunId, setSelectedRunId] = useState<
    DataAssetRunInfo["run_id"] | null
  >(null);

  const allConfigs = definitions.flatMap(d => d.workflow_instances || []);

  const materialiseState = useMemo(() => {
    const checkArchivedInput = (value: WorkflowInputValues) => {
      if (!isDataAsset(value)) {
        return false;
      }

      if (value.config_id) {
        const config = allConfigs.find(
          c => c.id.toString() === value.config_id,
        );
        if (config?.is_archived) {
          return true;
        }
      }

      if (value.config_group_id) {
        const groupConfigs = allConfigs.filter(
          c => c.group_id === value.config_group_id,
        );
        if (
          groupConfigs.length === 0 ||
          groupConfigs.every(c => c.is_archived)
        ) {
          return true;
        }
      }

      if (value.definition_id) {
        const definition = definitions.find(
          d => d.id.toString() === value.definition_id,
        );
        if (definition?.is_archived) {
          return true;
        }
      }

      if (value.definition_group_id) {
        const groupDefinitions = definitions.filter(
          d => d.group_id === value.definition_group_id,
        );
        if (
          groupDefinitions.length === 0 ||
          groupDefinitions.every(d => d.is_archived)
        ) {
          return true;
        }
      }

      return false;
    };

    const issues = [
      !projectId && "Project not selected",
      !documentId && "Document not selected",
      configHasChanges && "Configuration has unsaved changes",
      !selectedConfigId && "No configuration selected",
      !assetName?.trim() && "Asset name is required",
      selectedConfig?.is_archived && "Configuration is archived",
      selectedDefinition?.is_archived && "Definition is archived",
      Object.values(selectedConfig?.config?.inputs?.values || {}).some(
        checkArchivedInput,
      ) && "Configuration uses archived inputs",
    ].filter(Boolean);

    return {
      disabled: issues.length > 0,
      warningMessage: issues.join(", "),
    };
  }, [
    projectId,
    documentId,
    configHasChanges,
    selectedConfigId,
    assetName,
    selectedConfig,
    selectedDefinition,
    definitions,
  ]);
  const canFetchRuns = () => {
    return selectedDefinitionId && selectedConfigId;
  };

  const refreshButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const {
      projectId: newProject,
      documentId: newDocument,
    } = getEntityFromLocationSearch(location.search);

    const projectUpdate = !(newProject === projectId);
    const documentUpdate = !(newDocument === documentId);

    if (!projectUpdate && !documentUpdate) {
      return;
    }

    clearAssetPath();

    if (selectedConfigId && selectedDefinitionId) {
      fetchConfigRuns();
    } else {
      clearConfigRuns();
    }

    setDocumentId(newDocument);
    setProjectId(newProject);
  }, [location.search]);

  useEffect(() => {
    const {
      from: newFrom,
      to: newTo,
      user: newUser,
    } = getFiltersFromLocationSearch(location.search);
    if (
      (newFrom !== filters.from ||
        newTo !== filters.to ||
        newUser !== filters.user) &&
      canFetchRuns()
    ) {
      fetchConfigRuns();
    }
    setFilters({from: newFrom, to: newTo, user: newUser});
  }, [location.search]);

  useEffect(() => {
    if (params.definitionId) {
      const definitionId = parseInt(params.definitionId, 10);
      setSelectedDefinitionId(definitionId);
    } else {
      setSelectedDefinitionId(null);
    }
  }, [params.definitionId]);

  useEffect(() => {
    if (params.configId && params.definitionId) {
      const configId = parseInt(params.configId, 10);
      setSelectedConfigId(configId);
    } else if (params.definitionId) {
      setSelectedConfigId("");
    }
  }, [params.configId, params.definitionId]);

  useEffect(() => {
    if (params.assetId && params.configId && params.definitionId) {
      const assetId = parseInt(params.assetId, 10);
      setSelectedRunId(assetId);

      if (selectedRunId !== assetId) {
        fetchLlmRun(organisationId, assetId);
      }
    } else {
      setSelectedRunId(null);
    }
  }, [params.configId, params.definitionId, params.assetId]);

  useEffect(() => {
    if (selectedDefinition && selectedConfig) {
      const defaultAssetName =
        selectedConfig.config?.default_asset_name ||
        `${selectedDefinition.name} - ${selectedConfig.name}`;
      setAssetName(defaultAssetName);
    }
  }, [selectedDefinition, selectedConfig]);

  const handleRerunAllTests = () => {
    const runIds = runs.map(run => run.run_id);
    rerunAllTests(runIds);
    setShowRerunAllTestsFeedback(true);

    setTimeout(() => {
      setShowRerunAllTestsFeedback(false);
    }, 1500);
  };

  const handleSelectedRunChange = (id: LlmRunId | null) => {
    setSelectedRunId(id);
    const queryParams = getQueryParams();

    if (id && id !== selectedRunId) {
      fetchLlmRun(organisationId, id);
    }

    push(
      buildUrl(
        basePath,
        queryParams,
        selectedDefinitionId,
        selectedConfigId,
        id,
      ),
    );
  };

  const handleDefinitionChange = (definitionId: number) => {
    setSelectedDefinitionId(definitionId);
    const queryParams = getQueryParams();
    push(buildUrl(basePath, queryParams, definitionId));
    setSelectedConfigId("");
  };

  const handleConfigChange = (configId: number) => {
    const queryParams = getQueryParams();
    push(buildUrl(basePath, queryParams, selectedDefinitionId, configId));
  };

  const handleAssetNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setAssetName(event.target.value);
  };

  const handleRefresh = () => {
    fetchConfigRuns(true);

    if (selectedRunId) {
      fetchLlmRun(organisationId, selectedRunId);
    }
  };

  const handleMaterialise = (withLatestDependencies = false) => {
    materialiseDataAsset(assetName, undefined, withLatestDependencies);
    setShowStartedFeedback(true);

    setTimeout(() => {
      setShowStartedFeedback(false);
    }, 1500);
  };

  const handleShowGroupRunsChecked = (value: boolean) => {
    setShowGroupRuns(value);
  };

  const handleMaterialiseWithLatest = () => {
    materialiseDataAsset(assetName, matchingLatestDefinitionConfig.id);
    setShowStartedWithLatestFeedback(true);

    setTimeout(() => {
      setShowStartedWithLatestFeedback(false);
    }, 1500);
  };

  const runs =
    selectedConfig &&
    !isLoadingConfigRuns &&
    !fetchConfigRunsError &&
    Array.isArray(configRuns)
      ? configRuns
      : [];

  const onCloseOutput = () => {
    updateValues({view: null});
    handleSelectedRunChange(null);
  };

  const onSetView = (view: string) => {
    updateValues({view});
  };
  return (
    <>
      {selectedLlmRun?.id === selectedRunId &&
        selectedConfig &&
        selectedDefinition && (
          <ConfigurationRunOutput
            definition={selectedDefinition}
            config={selectedConfig}
            llmRun={selectedLlmRun}
            organisationId={organisationId}
            dataKey="output"
            title="Output"
            groupedResults={selectedLlmRun.grouped_results}
            onAddTest={onAddTest}
            onUpdateTest={onUpdateTest}
            onDeleteTest={onDeleteTest}
            getTestResults={getTestResults}
            fetchLlmRun={fetchLlmRun}
            rerunTests={rerunTests}
            onClose={onCloseOutput}
            view={view}
            setView={onSetView}
            ignoreClickawayRefs={[refreshButtonRef]}
            isRefreshing={isRefreshingRun}
            isRerunningTests={isRerunningTests}
          />
        )}

      <Box style={{padding: "1em", position: "relative", height: "100%"}}>
        <DefinitionSection
          definitions={filteredDefinitions}
          selectedDefinitionId={selectedDefinitionId}
          handleDefinitionChange={handleDefinitionChange}
          organisationId={organisationId}
        />
        <ConfigurationSection
          key={`${selectedDefinitionId}-config-section`}
          selectedConfigId={selectedConfigId}
          selectedDefinition={selectedDefinition}
          definitions={filteredDefinitions}
          configurations={configurations}
          configExpanded={Boolean(selectedDefinitionId)}
          basePath={basePath}
          push={push}
          handleConfigChange={handleConfigChange}
          createDefinitionConfig={createDefinitionConfig}
          updateAllConfigNames={updateAllConfigNames}
          setConfigHasChanges={setConfigHasChanges}
          updateConfigIsArchived={updateConfigIsArchived}
          updateConfigIsPublic={updateConfigIsPublic}
          updateConfigDefaultAssetName={updateConfigDefaultAssetName}
          organisationId={organisationId}
          isLatestDefinition={isLatestDefinition}
          latestDefinition={latestDefinition}
          latestConfig={matchingLatestDefinitionConfig}
        />
        <Box
          display="flex"
          justifyContent={"space-between"}
          alignItems={"center"}
          mt={2}
          flexWrap="wrap"
          style={{gap: "1em"}}
        >
          {selectedConfig && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "1em",
              }}
            >
              <Tooltip
                title={
                  showGroupRuns
                    ? "Show runs only from this configuration"
                    : "Show runs from other configurations in this group"
                }
              >
                <IconButton
                  onClick={() => handleShowGroupRunsChecked(!showGroupRuns)}
                >
                  {showGroupRuns ? (
                    <DatasetLinkedIcon fontSize="large" />
                  ) : (
                    <DatasetIcon fontSize="large" />
                  )}
                </IconButton>
              </Tooltip>
              <RunWithFeedback showStartedFeedback={showRerunAllTestsFeedback}>
                <Button
                  variant="outlined"
                  onClick={handleRerunAllTests}
                  disabled={!runs.length}
                >
                  Rerun All Tests
                </Button>
              </RunWithFeedback>
              <RefreshWithFeedbackButton
                isLoading={isRefreshingRuns}
                handleRefresh={handleRefresh}
                ref={refreshButtonRef}
              />
              <ComparisonLink
                organisationId={organisationId}
                definitionId={selectedDefinitionId}
                configId={selectedConfigId}
                projectId={projectId}
                documentId={documentId}
                from={filters.from}
                to={filters.to}
              />
              <TestListLink
                organisationId={organisationId}
                definitionId={selectedDefinitionId}
                configId={selectedConfigId}
                projectId={projectId}
                documentId={documentId}
              />
            </div>
          )}
          {selectedConfig && selectedDefinition && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "1em",
                flexGrow: 1,
              }}
            >
              <TextField
                style={{width: "100%"}}
                label="Asset Name"
                variant="outlined"
                size="small"
                value={assetName}
                onChange={handleAssetNameChange}
                error={!assetName.trim()}
              />
              {!isLatestDefinition && matchingLatestDefinitionConfig && (
                <RunWithFeedback
                  showStartedFeedback={showStartedWithLatestFeedback}
                >
                  <Tooltip
                    title={
                      <span style={{fontSize: "1rem"}}>
                        {materialiseState.warningMessage ||
                          latestDefinition.name}
                      </span>
                    }
                    arrow
                    placement="top"
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleMaterialiseWithLatest}
                        disabled={materialiseState.disabled}
                        endIcon={
                          materialiseState.warningMessage ? (
                            <WarningIcon />
                          ) : (
                            <InfoIcon />
                          )
                        }
                      >
                        Materialise with Latest
                      </Button>
                    </span>
                  </Tooltip>
                </RunWithFeedback>
              )}
              <RunWithFeedback showStartedFeedback={showStartedFeedback}>
                <Tooltip
                  title={
                    materialiseState.warningMessage ? (
                      <span style={{fontSize: "1rem"}}>
                        {materialiseState.warningMessage}
                      </span>
                    ) : (
                      ""
                    )
                  }
                  arrow
                  placement="top"
                >
                  <span>
                    <MaterialiseSplitButton
                      onMaterialise={handleMaterialise}
                      disabled={materialiseState.disabled}
                      warningMessage={materialiseState.warningMessage}
                    />
                  </span>
                </Tooltip>
              </RunWithFeedback>
            </div>
          )}
        </Box>

        {selectedConfig && (
          <>
            <ConfigurationRuns
              definitions={definitions}
              configurations={allConfigs}
              selectedDefinitionId={selectedDefinitionId}
              selectedConfig={selectedConfig}
              runs={runs}
              isLoadingRuns={isLoadingConfigRuns}
              error={fetchConfigRunsError}
              selectedRunId={selectedRunId}
              setSelectedRunId={handleSelectedRunChange}
              organisationId={organisationId}
              updateValues={updateValues}
            />
          </>
        )}
      </Box>
    </>
  );
};

function buildUrl(basePath, queryParams, definitionId?, configId?, assetId?) {
  if (!definitionId) {
    return `${basePath}/create${queryParams}`;
  }

  const assetPath = `asset/${assetId}`;
  const configPath = `config/${configId}`;
  const definitionPath = `definition/${definitionId}`;

  const fullPath = [
    basePath,
    definitionId ? definitionPath : null,
    configId ? configPath : null,
    assetId ? assetPath : null,
  ]
    .filter(part => part)
    .join("/");

  return `${fullPath}${queryParams}`;
}

export default DataAssetsCreateView;
